import './CatalogPage.scss'

import React, { useState, useEffect, useMemo } from 'react'
import moment from 'moment'

import { Link, useNavigate } from 'react-router-dom'

import merchantApi from '../../services/merchantApi'
import NoImage from '../../assets/images/no-image.png'
import Header from '../../components/Header/Header'
import { Grid } from '@mui/material'
import AppPagination from '../../components/AppPagination/AppPagination'
import { Icon } from '@iconify/react/dist/iconify.js'

const moduleCode = 'catalog'

const baseCategories = [
  { label: 'All', value: 'all' },
  { label: 'Unavailable', value: 'unavailable' },
]

const pickArray = (response) => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.data?.data)) return response.data.data
  if (Array.isArray(response?.products)) return response.products
  if (Array.isArray(response?.data?.products)) return response.data.products
  return []
}

const pickMeta = (response) => {
  const source = response?.data || {}

  return {
    currentPage: Number(source.current_page || source.page || 1),
    last_page: Number(source.last_page || source.total_pages || 1),
    total: Number(source.total || 0),
    from: Number(source.from || 0),
    to: Number(source.to || 0),
  }
}

const getTranslationValue = (product, keys) => {
  const translations = product?.translations || product?.translation || []
  const firstTranslation = Array.isArray(translations) ? translations[0] : translations

  for (const key of keys) {
    if (product?.[key]) return product[key]
    if (product?.data?.[key]) return product.data[key]
    if (firstTranslation?.[key]) return firstTranslation[key]
  }

  return ''
}

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return 'RM0.00'
  const number = Number(value)
  if (Number.isNaN(number)) return String(value)
  return `RM${number.toFixed(2)}`
}

const normalizeProduct = (product) => {
  const category = product?.categories || []

  const price = product?.price ?? product?.selling_price ?? product?.normal_price ?? product?.amount ?? 0
  const oldPrice = product?.normal_price && Number(product.normal_price) !== Number(price)
    ? product.normal_price
    : null

  return {
    ...product,
    id: product?.id,
    name:
      getTranslationValue(product, ['name', 'title', 'product_name']) ||
      product?.sku ||
      `Product #${product?.id}`,
    price: formatMoney(price),
    oldPrice: oldPrice ? formatMoney(oldPrice) : null,
    rawPrice: price,
    category,
    // categoryValue: String(category).toLowerCase(),
    image:
      product?.image_url ||
      product?.thumbnail_image ||
      product?.image ||
      product?.media?.[0]?.url ||
      product?.images?.[0]?.url ||
      NoImage,
    active: Boolean(Number(product?.status ?? product?.visibility ?? 1)),
  }
}

export default function CatalogPage() {
  const [openMenuId, setOpenMenuId] = useState(null)
  const [activeCategory, setActiveCategory] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [products, setProducts] = useState([])
  const [userInfo, setUserInfo] = useState([])
  const [meta, setMeta] = useState({ currentPage: 1, last_page: 1, total: 0, from: 0, to: 0 })
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState('')
  const [editable, setEditable] = useState(true)
  const [selectedOutletId, setSelectedOutletId] = useState(null)
  const navigate = useNavigate()

  const ITEMS_PER_PAGE = 12

  useEffect(() => {
    loadUserInfo()
  }, [])

  const loadProducts = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true)
      setError('')

      // check if the activeCategory visibility is 0
      let visibility = meta?.tabs?.find((tab) => tab.id === activeCategory)?.visibility

      let payload = {
        page: currentPage,
        size: ITEMS_PER_PAGE,
        search: searchQuery,
        "filter[outlet_id]": selectedOutletId,
      }

      if (visibility === 0) {
        payload["filter[visibility]"] = 0
      } else {
        payload["filter[category_ids][]"] = activeCategory === 'all' ? null : activeCategory
      }

      const response = await merchantApi.products.list(payload)

      const productRows = pickArray(response).map(normalizeProduct)
      const responseMeta = pickMeta(response)
      setProducts(productRows)
      setMeta({
        last_sync: response?.meta?.last_sync || null,
        currentPage: responseMeta.currentPage || currentPage,
        last_page: responseMeta.last_page || 1,
        total: responseMeta.total || productRows.length,
        from: responseMeta.from || 0,
        to: responseMeta.to || ((currentPage - 1) * ITEMS_PER_PAGE + productRows.length),
        tabs: response?.meta?.tabs?.map((tab) => ({ id: tab.id || tab.key, label: tab.name, value: tab.key, count: tab.product_count || 0, visibility: tab?.filter ? tab.filter.visibility : 1 })) || [],
      })
      if (!activeCategory)
        setActiveCategory(response?.meta?.tabs?.[0]?.id || 'all')
    } catch (err) {
      setError(err?.message || 'Unable to load products.')
      setProducts([])
      setMeta({ currentPage: 1, last_page: 1, total: 0, from: 0, to: 0 })
    } finally {
      setLoading(false)
      setSyncing(false)
    }
  }

  const loadUserInfo = async () => {

    try {
      const response = await merchantApi.auth.me()
      if (response?.data?.permissions?.includes(`view-${moduleCode}`)) {
        setUserInfo(response?.data || null);
        setEditable(response?.data?.permissions?.includes(`edit-${moduleCode}`))
        setSelectedOutletId(response?.data?.default_outlet?.id || null)
      } else {
        navigate('/unauthorized')
      }
    } catch (err) {
      setError(err?.message || 'Failed to load user info.')
      setUserInfo(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null)
    }

    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (selectedOutletId) {
      loadProducts()
    }
  }, [currentPage, selectedOutletId])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (currentPage === 1 && selectedOutletId) {
        loadProducts()
      } else {
        setCurrentPage(1)
      }
    }, 350)

    return () => clearTimeout(timeout)
  }, [searchQuery])

  useEffect(() => {
    setCurrentPage(currentPage === 1 ? "1" : 1)
  }, [activeCategory])

  const categoryTabs = useMemo(() => {
    return meta?.tabs || []
  }, [meta?.tabs])

  const handleSync = () => {
    setLoading(true)
    merchantApi.products.sync()
      .then(() => {
        loadProducts({ silent: true })
      })
      .catch((err) => {
        setError(err?.message || 'Failed to sync products.')
      })
      .finally(() => {
        setLoading(false)
      })
  }
  console.log("catalog", meta)
  return (
    <div className="catalog-page">
      <Header
        items={[
          { label: userInfo?.merchant?.business_name || 'Merchant', to: '/' },
          { label: 'Catalog', current: true },
        ]}
      />
      <div className="catalog-header">
        <div>
          <h1 className="catalog-header__title">
            Catalog management
          </h1>

          <p className="catalog-header__description">
            Last synced on {meta?.last_sync ? moment(meta.last_sync).format('MMM D, YYYY [at] h:mm A') : 'Never'}
          </p>
        </div>

        <div className="catalog-header__actions">
          <div className="catalog-search">
            <Icon icon="ph:magnifying-glass" height={18} />

            <input
              type="text"
              placeholder="Search products"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button className="catalog-sync" type="button" onClick={handleSync} disabled={syncing || loading}>
            <Icon icon="ph:arrows-clockwise" height={14} />
            {syncing ? 'Syncing...' : 'Sync POS'}
          </button>
        </div>
      </div>

      <div className="catalog-tabs">
        {categoryTabs.map((category) => (
          <button
            key={category.value}
            type="button"
            className={`catalog-tabs__item ${activeCategory === category.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(category.id)}
          >
            {category.label} ({category.count})
          </button>
        ))}
      </div>

      {error && (
        <div className="catalog-empty">
          {error}
        </div>
      )}

      {loading && !error && (
        <div className="catalog-empty">
          Loading products...
        </div>
      )}
      {!loading && !error && (
        <Grid container spacing={3} className="catalog-grid">
          {products.map((product) => (
            <Grid key={product.id} item xs={12} sm={6} lg={4} xl={3} className="catalog-grid__item">
              <div className="catalog-card" onClick={() => { editable && navigate(`/products/${product.id}/edit`) }}>
                <div className="catalog-card__image-wrapper">
                  <img
                    src={product.image || NoImage}
                    alt={product.name}
                    className="catalog-card__image"
                  />
                </div>
                <div className="catalog-card__body">
                  <div className="catalog-card__top">

                    <div className="catalog-card__content">
                      <h3 className="catalog-card__title">
                        {product.name}
                      </h3>

                      <div className="catalog-card__price">
                        {product.oldPrice && (
                          <span className="catalog-card__old-price">
                            {product.oldPrice}
                          </span>
                        )}

                        <span>{product.price}</span>
                      </div>
                    </div>

                    <div
                      className="catalog-card__menu"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="catalog-card__menu-trigger"
                        onClick={() =>
                          setOpenMenuId(
                            openMenuId === product.id
                              ? null
                              : product.id
                          )
                        }
                      >
                        <Icon icon={"ph:dots-three-vertical"} height={18} />
                      </button>

                      {openMenuId === product.id && editable && (
                        <div className="catalog-card__dropdown">
                          <Link
                            to={`/products/${product.id}/edit`}
                            className="catalog-card__dropdown-item"
                          >
                            <Icon icon={"ph:pencil-simple"} height={16} />
                            Edit
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="catalog-card__footer">
                    {/*                <label className="catalog-switch">
                  <input
                    type="checkbox"
                    disabled
                    defaultChecked={
                      product.active
                    }
                  />

                  <span className="catalog-switch__slider" />
                </label>*/}
                    {
                      product?.best_seller_badge && (
                        <span className="catalog-card__status catalog-card__status--enabled">
                          {'Best Seller'}
                        </span>
                      )
                    }
                    {
                      !product.active &&
                      <div
                        className={`catalog-card__status ${product.active
                          ? 'catalog-card__status--enabled'
                          : 'catalog-card__status--disabled'
                          }`}
                      >
                        <Icon icon={"ph:circle-fill"} height={6} />
                        {product.active
                          ? 'Active'
                          : 'Inactive'}
                      </div>
                    }

                    {
                      product?.category?.length > 0 && product.category.map((item, index) => (
                        <span className="catalog-card__tag">
                          {item?.description?.name}
                        </span>
                      ))
                    }

                  </div>
                </div>
              </div>
            </Grid>
          ))}

          {products.length === 0 && (
            <Grid item xs={12}>
              <div className="catalog-empty">
                No products found.
              </div>
            </Grid>
          )}
        </Grid>
      )}

      <div className="catalog-pagination">
        <AppPagination
          currentPage={currentPage}
          pagination={meta}
          onPageChange={setCurrentPage}
          showResultsText={true}
        />
      </div>
    </div>
  )
}
